import { memo } from 'react'
import type { CampaignPacing } from '../../lib/adsApi.js'
import { deliveryGeometry, pacingTone, spotCount } from '../../lib/adsPacing.js'

/**
 * Barre d'engagement d'une campagne.
 *
 * Une seule image remplace les quatre affichages du même ratio : la piste vaut
 * le volume **vendu**, le rempli dit où en est la campagne (diffusé, puis posé
 * non encore diffusé), et le repère dit jusqu'où l'inventaire restant peut la
 * mener. Tant que le repère atteint le bout de la piste, l'engagement est
 * tenable ; ce qui le dépasse est hors de portée et se lit en hachures.
 *
 * Le pourcentage n'est jamais écrit : il n'apprend rien à la régie, qui compte
 * des passages. L'accessibilité passe par l'étiquette de la piste, et l'état
 * n'est jamais porté par la seule couleur — les hachures et le texte le disent
 * aussi.
 */

interface DeliveryBarProps {
  pacing: CampaignPacing
  /** Ajoute la légende chiffrée sous la piste. */
  detailed?: boolean
}

export const DeliveryBar = memo(function DeliveryBar({ pacing, detailed = false }: DeliveryBarProps) {
  const geometry = deliveryGeometry(pacing)
  const tone     = pacingTone(pacing.verdict)

  const label = [
    `${pacing.airedSpots} diffusés`,
    `${pacing.placedSpots} posés sur ${pacing.soldSpots} vendus`,
    geometry.shortfall > 0
      ? `${spotCount(geometry.shortfall)} hors de portée`
      : `${spotCount(pacing.reachableSpots)} encore posables`,
  ].join(', ')

  return (
    <div className={`delivery${detailed ? ' delivery--detailed' : ''}`}>
      <div
        className={`delivery__track is-${tone}`}
        role="img"
        aria-label={label}
        style={{
          '--delivery-aired':    `${geometry.airedPct}%`,
          '--delivery-placed':   `${geometry.placedPct}%`,
          '--delivery-capacity': `${geometry.capacityPct}%`,
        } as React.CSSProperties}
      >
        <div className="delivery__placed" />
        <div className="delivery__aired" />
        {geometry.shortfall > 0 && <div className="delivery__shortfall" />}
        <div className="delivery__capacity" />
      </div>

      {detailed && (
        <dl className="delivery__legend">
          <LegendItem mark="aired"  term="Diffusés" value={pacing.airedSpots} />
          <LegendItem mark="placed" term="Posés"    value={pacing.placedSpots} />
          <LegendItem mark="sold"   term="Vendus"   value={pacing.soldSpots} />
          {/* La portée ne se lit que tant qu'il reste à poser : annoncer « 240
              encore posables » sur une campagne soldée est un chiffre juste et
              inutile, exactement le travers qu'on retire de cette page. */}
          {geometry.shortfall > 0 ? (
            <LegendItem mark="shortfall" term="Hors de portée" value={geometry.shortfall} tone="danger" />
          ) : pacing.remainingSpots > 0 ? (
            <LegendItem mark="capacity" term="Encore posables" value={pacing.reachableSpots} />
          ) : null}
        </dl>
      )}
    </div>
  )
})

const LegendItem = memo(function LegendItem({ mark, term, value, tone }: {
  mark:  string
  term:  string
  value: number
  tone?: string
}) {
  return (
    <div className={`delivery__legend-item${tone ? ` is-${tone}` : ''}`}>
      <span className={`delivery__mark delivery__mark--${mark}`} aria-hidden="true" />
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  )
})
